<script setup>
import { tv } from 'tailwind-variants';
import { computed, nextTick, onMounted, ref, useSlots, watch } from 'vue';
import { CsFormError, CsFormHelpText, CsFormLabel } from '../form';
import { CsIcon } from '../icon';
import { generateId } from '../shareds';

/**
 * @typedef {object} TextareaProps
 * @property {string} [value=''] - The value of the textarea
 * @property {string} [placeholder=''] - Placeholder text for the textarea
 * @property {string} [label=''] - Label text for the textarea
 * @property {string} [labelTooltip=''] - Tooltip text for the label
 * @property {string} [labelTooltipPlacement='right'] - Placement of the label tooltip
 * @type {'bottom-start'|'top'|'right'|'bottom'|'left'|'top-start'|'top-end'|'right-start'|'right-end'|'bottom-end'|'left-start'|'left-end'} [labelTooltipPlacement='right'] - Placement of the tooltip relative to the label
 * @property {string} [helpText=''] - Help text displayed below the textarea
 * @property {string} [name=''] - Name attribute for the textarea
 * @property {string} [id=''] - ID attribute for the textarea
 * @property {boolean} [isClearable=true] - Whether the textarea can be cleared
 * @property {('none'|'vertical'|'horizontal'|'both')} [resize='none'] - Resize behavior of the textarea
 * @property {number} [minHeight=null] - Minimum height of the textarea in pixels
 * @property {number} [maxHeight=null] - Maximum height of the textarea in pixels
 * @property {number} [maxLength=null] - Maximum number of characters allowed
 * @property {boolean} [isReadonly=false] - Whether the textarea is read-only
 * @property {boolean} [isDisabled=false] - Whether the textarea is disabled
 * @property {boolean} [isAutosize=false] - Whether the textarea should automatically resize based on content
 * @property {boolean} [isShowCounter=false] - Whether to show character counter when maxLength is set
 * @property {string} [error=''] - Error message for the textarea
 */

/**
 * @type {TextareaProps}
 */
const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  labelTooltip: {
    type: String,
    default: '',
  },
  labelTooltipPlacement: {
    type: String,
    default: 'right',
  },
  helpText: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  isClearable: {
    type: Boolean,
    default: true,
  },
  resize: {
    type: String,
    default: 'none',
  },
  minHeight: {
    type: Number,
    default: null,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: Number,
    default: null,
  },
  isAutosize: {
    type: Boolean,
    default: false,
  },
  isShowCounter: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['input', 'change', 'clear', 'focus', 'blur']);

const textareaRecipe = tv({
  slots: {
    root: 'group relative flex flex-col',
    textarea: [
      'min-h-[var(--min-height)]',
      'w-full placeholder:text-brand-gray-13 rounded-lg border',
      'focus:outline-none',
    ],
  },
  variants: {
    size: {
      default: {
        textarea: 'px-3 py-2.5 text-sm',
      },
    },
    resize: {
      none: {
        textarea: 'resize-none',
      },
      vertical: {
        textarea: 'resize-y',
      },
      horizontal: {
        textarea: 'resize-x',
      },
      both: {
        textarea: 'resize',
      },
    },
    variant: {
      default: {
        textarea: [
          'text-brand-text',
          'bg-brand-gray-1 group-hover:bg-brand-gray-3 dark:bg-brand-gray-16 group-hover:dark:bg-brand-gray-16',
          'border-brand-gray-12 dark:border-brand-gray-13',
          'group-hover:border-brand-gray-13 dark:group-hover:border-brand-gray-14',
          'focus:border-brand-gray-16 dark:focus:border-brand-gray-16',
        ],
      },
      readonly: {
        textarea: [
          'cursor-default text-brand-gray-11 read-only:bg-brand-black-alpha-6 dark:readonly:bg-brand-gray-15',
          'border-brand-gray-12 dark:border-brand-gray-13',
        ],
      },
      disabled: {
        textarea: [
          'cursor-not-allowed text-brand-gray-11 disabled:bg-brand-black-alpha-5 dark:disabled:bg-brand-gray-14',
          'border !border-transparent',
        ],
      },
      error: {
        textarea: [
          'text-brand-text',
          'bg-brand-red-4 dark:bg-brand-red-16',
          'group-hover:bg-brand-red-4 group-hover:dark:bg-brand-red-16',
          'border-brand-red-12 dark:border-brand-red-13',
          'group-hover:border-brand-red-13 dark:group-hover:border-brand-red-14',
          'focus:border-brand-red-13 dark:focus:border-brand-red-16',
          'focus:bg-brand-red-4',
        ],
      },
    },
  },
  defaultVariants: {
    size: 'default',
    resize: 'none',
    variant: 'default',
  },
});

const slots = useSlots();
function hasSlot(name) {
  return !!slots[name];
}

function getVariant() {
  if (props.isDisabled) return 'disabled';
  if (props.isReadonly) return 'readonly';
  if (props.error?.length) return 'error';
  return 'default';
}

const textareaClasses = computed(() =>
  textareaRecipe({
    size: props.size,
    resize: props.resize,
    variant: getVariant(),
  })
);

const textareaId = computed(() => props.id || generateId(10));
const textareaName = computed(() => props.name || generateId(10));

const formattedValueLength = computed(() =>
  (props.value?.length ?? 0).toLocaleString()
);
const formattedMaxLength = computed(
  () => props.maxLength?.toLocaleString() ?? '0'
);

const textarea = ref(null);

const isFocused = ref(false);

function handleUpdateModelValue(event) {
  emit('input', event.target.value);
  emit('change', event.target.value);

  if (props.isAutosize) {
    adjustHeight();
  }
}

function onFocus() {
  isFocused.value = true;

  emit('focus');
}

function onBlur() {
  isFocused.value = false;

  emit('blur');
}

function adjustHeight() {
  if (!textarea.value) return;

  // Reset height to get the correct scrollHeight
  textarea.value.style.height = '20px';

  // Calculate new height but respect maxHeight if set
  const newHeight = textarea.value.scrollHeight;

  if (props.maxHeight && newHeight > props.maxHeight) {
    textarea.value.style.height = `${props.maxHeight}px`;
  } else {
    textarea.value.style.height = `${newHeight}px`;
  }
}

watch(
  () => props.value,
  (newValue, oldValue) => {
    if (props.isAutosize && newValue.length && !oldValue.length) {
      nextTick(() => adjustHeight());
    }
  }
);

defineExpose({
  adjustHeight,
  textarea,
  focus: () => {
    textarea.value.focus();
  },
});

onMounted(() => {
  if (props.isAutosize) {
    adjustHeight();
  }
});
</script>

<template>
  <div
    :class="textareaClasses.root()"
    :data-required="props.isRequired || undefined"
    :data-variant="getVariant()"
  >
    <div v-if="props.label" class="flex items-center gap-2 mb-1">
      <CsFormLabel :for="textareaId" :is-required="props.isRequired">
        {{ props.label }}
      </CsFormLabel>
      <CsIcon
        v-if="props.labelTooltip"
        v-cs-tooltip="{
          content: props.labelTooltip,
          placement: props.labelTooltipPlacement,
        }"
        name="info"
        class="text-brand-gray-12 dark:text-brand-gray-11"
      />
    </div>

    <div class="relative inline-flex flex-col">
      <textarea
        :id="textareaId"
        ref="textarea"
        :style="{
          '--min-height': props.minHeight ? `${props.minHeight}px` : 'auto',
          '--max-height': props.maxHeight ? `${props.maxHeight}px` : 'auto',
          margin: '0',
          maxHeight: 'var(--max-height)',
        }"
        :class="textareaClasses.textarea()"
        :name="textareaName"
        :required="props.isRequired"
        :readonly="props.isReadonly"
        :disabled="props.isDisabled"
        :placeholder="props.placeholder"
        :maxlength="props.maxLength"
        v-bind="$attrs"
        :value="props.value"
        @input="handleUpdateModelValue"
        @change="handleUpdateModelValue"
        @focus="onFocus"
        @blur="onBlur"
      />

      <slot name="placeholder" />

      <div
        v-if="isShowCounter"
        class="absolute right-3 bottom-1.5 flex items-center gap-1"
        :class="props.isDisabled ? 'opacity-50' : ''"
      >
        <span class="text-xs text-brand-text-secondary">
          {{ formattedValueLength }}/{{ formattedMaxLength }}
        </span>
      </div>
    </div>

    <CsFormError v-if="props.error" class="mt-1">
      {{ props.error }}
    </CsFormError>

    <CsFormHelpText v-if="props.helpText || hasSlot('helpText')" class="mt-1">
      <slot v-if="hasSlot('helpText')" name="helpText" />
      <span v-else>{{ props.helpText }}</span>
    </CsFormHelpText>
  </div>
</template>
